import type { DestinationKey, PrivacyClass, TrackEvent } from './types'

export interface EventDestinationOverrides {
  /**
   * If provided, this destination will receive the returned eventName/properties.
   * Return null to skip this destination for this event.
   */
  map?: (
    event: TrackEvent
  ) => { eventName: string; properties?: Record<string, any> } | null
}

export interface EventDefinition {
  /**
   * Default destinations for this event. If omitted, the tracking client default applies.
   */
  defaultDestinations?: DestinationKey[]

  /**
   * Privacy classification per property name (used for filtering/transforming).
   */
  privacy?: Record<string, PrivacyClass>

  /**
   * Per-destination overrides (optional).
   */
  destinations?: Partial<Record<DestinationKey, EventDestinationOverrides>>
}

function checkoutPaymentMethodLabel(method: unknown) {
  switch (method) {
    case 'existing':
      return 'saved card'
    case 'new':
      return 'new card'
    case 'paypal':
      return 'PayPal'
    case 'klarna':
      return 'Klarna'
    case 'applepay':
      return 'Apple Pay'
    case 'adminFree':
      return 'admin free order'
    case 'adminManual':
      return 'admin manual payment'
    default:
      return 'unknown'
  }
}

function safeCheckoutLabel(value: unknown, fallback = 'unknown') {
  if (typeof value !== 'string') return fallback
  const trimmed = value.trim()
  return trimmed || fallback
}

function summerSalePopupKissmetricsProperties(event: TrackEvent) {
  const p = event.properties || {}
  return {
    Campaign: p.campaign,
    Placement: p.placement,
    'Brand code': p.brandCode,
    'Product ID': p.productId,
    'Product name': p.productName,
    'Product slug': p.productSlug,
    'Destination URL': p.destinationUrl,
  }
}

function summerSalePopupGa4Properties(event: TrackEvent) {
  const p = event.properties || {}
  return {
    campaign: p.campaign,
    placement: p.placement,
    brand_code: p.brandCode,
    product_id: p.productId?.toString?.() ?? p.productId,
    product_name: p.productName,
    product_slug: p.productSlug,
    destination_url: p.destinationUrl,
  }
}

/**
 * Event Registry:
 * - Primary key is the Kissmetrics event name (do not rename).
 * - Each destination can optionally override eventName/properties without changing the callsite.
 */
export const eventRegistry: Record<string, EventDefinition> = {
  logged_in: {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: () => ({
          eventName: 'login',
          properties: {},
        }),
      },
    },
  },
  identify: {
    // Existing behavior on the old site: Customer.io identify happens for logged in users.
    // Zoho PageSense identifyUser is checkout-only (see OrderSummary.ascx on vanishinginc-dev).
    defaultDestinations: [
      'kissmetrics',
      'ga4',
      'facebookPixel',
      'customerIo',
      'zohoPageSense',
    ],
  },
  page: {
    defaultDestinations: ['ga4', 'facebookPixel', 'customerIo'],
  },
  'Page not found': {
    defaultDestinations: ['ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'page_not_found',
            properties: {
              error_code: 404,
              page_path: p.page_path,
              page_location: p.page_location,
              page_referrer: p.page_referrer,
              page_title: p.page_title,
            },
          }
        },
      },
    },
  },
  'Viewed checkout': {
    defaultDestinations: ['kissmetrics', 'ga4', 'zohoPageSense'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Viewed checkout',
            properties: { Products: p.itemCount },
          }
        },
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'view_cart',
            properties: {
              currency: p.currency,
              value: p.value,
              items: p.items,
            },
          }
        },
      },
    },
  },
  'Visited checkout email page': {
    defaultDestinations: ['zohoPageSense'],
  },
  'Viewed product': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Viewed product',
            properties: {
              'Product ID': p.productId,
              'Product name': p.productName,
              'Product price': p.price,
            },
          }
        },
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'view_item',
            properties: {
              currency: p.currency,
              value: p.price,
              items: [
                {
                  item_id: p.productId?.toString?.() ?? p.productId,
                  item_name: p.productName,
                  price: p.price,
                  quantity: 1,
                },
              ],
            },
          }
        },
      },
    },
  },
  'Showed Summer Sale popup': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => ({
          eventName: 'Showed Summer Sale popup',
          properties: summerSalePopupKissmetricsProperties(event),
        }),
      },
      ga4: {
        map: event => ({
          eventName: 'summer_sale_popup_shown',
          properties: summerSalePopupGa4Properties(event),
        }),
      },
    },
  },
  'Clicked Summer Sale popup': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => ({
          eventName: 'Clicked Summer Sale popup',
          properties: summerSalePopupKissmetricsProperties(event),
        }),
      },
      ga4: {
        map: event => ({
          eventName: 'summer_sale_popup_clicked',
          properties: summerSalePopupGa4Properties(event),
        }),
      },
    },
  },
  'Closed Summer Sale popup': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => ({
          eventName: 'Closed Summer Sale popup',
          properties: summerSalePopupKissmetricsProperties(event),
        }),
      },
      ga4: {
        map: event => ({
          eventName: 'summer_sale_popup_closed',
          properties: summerSalePopupGa4Properties(event),
        }),
      },
    },
  },
  'Added item to cart': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Added item to cart',
            properties: {
              'Add to cart product ID': p.productId,
              'Add to cart name': p.productName,
              'Add to cart price': p.price,
              'Add to cart quantity': p.quantity ?? 1,
              'Add to cart currency': p.currency,
            },
          }
        },
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          const quantity = Number(p.quantity ?? 1)
          const price = typeof p.price === 'number' ? p.price : undefined
          const value = price != null ? price * quantity : undefined

          return {
            eventName: 'add_to_cart',
            properties: {
              currency: p.currency,
              value,
              items: [
                {
                  item_id: p.productId?.toString?.() ?? p.productId,
                  item_name: p.productName,
                  price,
                  quantity,
                },
              ],
            },
          }
        },
      },
    },
  },
  'Removed item from cart': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Removed item from cart',
            properties: {
              'Removed product ID': p.productId,
              'Removed product name': p.productName,
            },
          }
        },
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          const quantity = Number(p.quantity ?? 1)
          const price = typeof p.price === 'number' ? p.price : undefined
          const value = price != null ? price * quantity : undefined

          return {
            eventName: 'remove_from_cart',
            properties: {
              currency: p.currency,
              value,
              items: [
                {
                  item_id: p.productId?.toString?.() ?? p.productId,
                  item_name: p.productName,
                  price,
                  quantity,
                },
              ],
            },
          }
        },
      },
    },
  },
  'Checkout started': {
    defaultDestinations: ['kissmetrics', 'ga4', 'zohoPageSense'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Checkout started',
            properties: {
              'Checkout button location': p.location,
              Products: p.itemCount,
            },
          }
        },
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'begin_checkout',
            properties: {
              currency: p.currency,
              value: p.value,
              items: p.items,
            },
          }
        },
      },
    },
  },
  'Completed final checkout step': {
    defaultDestinations: ['kissmetrics', 'ga4', 'zohoPageSense'],
    destinations: {
      kissmetrics: {
        map: () => ({ eventName: 'Completed final checkout step' }),
      },
      ga4: {
        // Keep old-site custom event name
        map: () => ({
          eventName: 'finished_checkout_payment_page',
          properties: {},
        }),
      },
    },
  },
  'Visited checkout payment page': {
    defaultDestinations: ['zohoPageSense'],
  },
  'Visited checkout payment issue page': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => {
          const issueType = safeCheckoutLabel(event.properties?.issueType)
          return { eventName: `Checkout payment issue: ${issueType}` }
        },
      },
    },
  },
  'Checkout interrupted': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout interrupted: ${safeCheckoutLabel(
            event.properties?.reason
          )}`,
        }),
      },
    },
  },
  'Checkout pay guard blocked': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout pay guard: ${safeCheckoutLabel(
            event.properties?.reason
          )}`,
        }),
      },
    },
  },
  'Checkout payment validation blocked': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout payment validation: ${safeCheckoutLabel(
            event.properties?.reason
          )}`,
        }),
      },
    },
  },
  'Checkout validation blocked': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout validation: ${safeCheckoutLabel(
            event.properties?.reason
          )}`,
        }),
      },
    },
  },
  'Checkout market moved': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout market moved: ${safeCheckoutLabel(
            event.properties?.destination
          )}`,
        }),
      },
    },
  },
  'Checkout payment problem': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout payment problem: ${safeCheckoutLabel(
            event.properties?.reason
          )}`,
        }),
      },
    },
  },
  'Checkout processing problem': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout processing: ${safeCheckoutLabel(
            event.properties?.reason
          )}`,
        }),
      },
    },
  },
  'Checkout payment recovery status': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Checkout payment recovery: ${safeCheckoutLabel(
            event.properties?.status
          )}`,
        }),
      },
    },
  },
  'Selected checkout payment method': {
    defaultDestinations: ['zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => ({
          eventName: `Selected payment method: ${checkoutPaymentMethodLabel(
            event.properties?.paymentMethod
          )}`,
        }),
      },
    },
  },
  'Started PayPal checkout': {
    defaultDestinations: ['kissmetrics', 'ga4', 'zohoPageSense'],
    destinations: {
      kissmetrics: { map: () => ({ eventName: 'Started PayPal checkout' }) },
      ga4: {
        map: () => ({ eventName: 'started_paypal_checkout', properties: {} }),
      },
      zohoPageSense: {
        map: event => {
          const label = (
            (event.properties?.pageSensePaymentLabel as string | undefined) ??
            'PayPal'
          ).trim()
          return { eventName: `Started ${label}` }
        },
      },
    },
  },
  'Finished PayPal checkout': {
    defaultDestinations: ['kissmetrics', 'ga4', 'zohoPageSense'],
    destinations: {
      kissmetrics: { map: () => ({ eventName: 'Finished PayPal checkout' }) },
      ga4: {
        map: () => ({ eventName: 'finished_paypal_checkout', properties: {} }),
      },
      zohoPageSense: {
        map: event => {
          const label = (
            (event.properties?.pageSensePaymentLabel as string | undefined) ??
            'PayPal'
          ).trim()
          return { eventName: `${label} success` }
        },
      },
    },
  },
  purchase: {
    // Browser purchase is fired explicitly on the thank-you page (GA4, Ads, Pixel).
    // vi-core-api also sends GA4 purchase via Measurement Protocol as a server-side backup.
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'purchase',
            properties: {
              transaction_id: p.transaction_id,
              value: p.value,
              currency: p.currency,
              tax: p.tax,
              shipping: p.shipping,
              coupon: p.coupon,
              items: p.items,
            },
          }
        },
      },
      googleAds: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'conversion',
            properties: {
              value: p.value,
              currency: p.currency,
              transaction_id: p.transaction_id,
              // Optional, used by GoogleAdsDestination to compute Enhanced Conversions.
              email: p.email,
            },
          }
        },
      },
      facebookPixel: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Purchase',
            properties: {
              value: p.value,
              currency: p.currency,
              num_items: p.num_items,
            },
          }
        },
      },
      customerIo: {
        map: event => {
          const p = event.properties || {}
          // Match old-site comet('purchase', { amount: ... })
          return {
            eventName: 'purchase',
            properties: {
              amount: p.amount ?? p.value,
            },
          }
        },
      },
    },
  },
  'Completed checkout': {
    defaultDestinations: ['zohoPageSense'],
  },
  'Free Tricks signup': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: () => ({
          eventName: 'Easy Magic Tricks signup',
          properties: {},
        }),
      },
      kissmetrics: {
        map: () => ({
          eventName: 'Registered for free download',
          properties: { 'Free download name': 'EasyMagicTricksBundle' },
        }),
      },
    },
  },
  'Watched Live Stream': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Watched Live Stream',
            properties: {
              'Product ID': p.productId,
              'Product slug': p.productSlug,
            },
          }
        },
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'watched_live_stream',
            properties: {
              product_id: p.productId,
              product_slug: p.productSlug,
            },
          }
        },
      },
    },
  },
  'Daily deals signup viewed': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'daily_deals_signup_viewed',
          properties: event.properties ?? {},
        }),
      },
    },
  },
  'Daily deals signup submitted': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'daily_deals_signup_submitted',
          properties: event.properties ?? {},
        }),
      },
    },
  },
  'Daily deals signup succeeded': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'daily_deals_signup_succeeded',
          properties: event.properties ?? {},
        }),
      },
    },
  },
  'Daily deals signup failed': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'daily_deals_signup_failed',
          properties: event.properties ?? {},
        }),
      },
    },
  },
  'Daily deals modal closed': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'daily_deals_modal_closed',
          properties: event.properties ?? {},
        }),
      },
    },
  },
  'Newsletter signup': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: () => ({
          eventName: 'Newsletter signup',
          properties: {},
        }),
      },
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'Registered for free download',
            properties: {
              'Free download name': p.offer ?? '16 killer tricks',
            },
          }
        },
      },
    },
  },
  'Registered for free download': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      kissmetrics: {
        map: event => ({
          eventName: 'Registered for free download',
          properties: event.properties ?? {},
        }),
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'registered_for_free_download',
            properties: {
              free_download_name: p['Free download name'] ?? p.offer,
              source: p.source ?? p.Source,
              add_to_newsletter: p.addToNewsletter,
            },
          }
        },
      },
    },
  },
  'Registered for free download from explanation page': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      kissmetrics: {
        map: event => ({
          eventName: 'Registered for free download',
          properties: event.properties ?? {},
        }),
      },
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'registered_for_free_download',
            properties: {
              free_download_name: p['Free download name'] ?? p.offer,
              source: p.source ?? p.Source ?? 'explanation-page',
              add_to_newsletter: p.addToNewsletter,
            },
          }
        },
      },
    },
  },
  'Signed up': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: () => ({
          eventName: 'sign_up',
          properties: {},
        }),
      },
    },
  },
  'Viewed signup page': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    privacy: {
      'Signup method': 'public',
      'How signed up': 'public',
    },
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'view_signup',
          properties: {
            signup_method: event.properties?.['Signup method'],
            how_signed_up: event.properties?.['How signed up'],
          },
        }),
      },
    },
  },
  'Started signup': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    privacy: {
      'Signup method': 'public',
      'How signed up': 'public',
    },
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'begin_signup',
          properties: {
            signup_method: event.properties?.['Signup method'],
            how_signed_up: event.properties?.['How signed up'],
          },
        }),
      },
    },
  },
  'Signup validation failed': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    privacy: {
      'Error count': 'public',
      'Error fields': 'public',
      'Signup method': 'public',
      'How signed up': 'public',
    },
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'signup_validation_error',
          properties: {
            error_count: event.properties?.['Error count'],
            error_fields: event.properties?.['Error fields'],
            signup_method: event.properties?.['Signup method'],
            how_signed_up: event.properties?.['How signed up'],
          },
        }),
      },
    },
  },
  'Deluxe edition list signup': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => ({
          eventName: 'deluxe_edition_list_signup',
          properties: {
            source: event.properties?.source,
          },
        }),
      },
    },
  },
  'Viewed category landing page': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      kissmetrics: {
        map: event => {
          const categoryName = event.properties?.categoryName ?? 'Unknown'
          return {
            eventName: `Viewed ${categoryName} landing page`,
            properties: {},
          }
        },
      },
      ga4: {
        map: event => ({
          eventName: 'view_category_landing',
          properties: {
            category_name: event.properties?.categoryName,
          },
        }),
      },
    },
  },
  'Clicked convention registration CTA': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'clicked_convention_registration_cta',
            properties: {
              convention_slug: p.conventionSlug,
              convention_product_slug: p.conventionProductSlug,
              placement: p.placement,
              destination_url: p.destinationUrl,
            },
          }
        },
      },
    },
  },
  'Viewed convention registration start page': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'viewed_convention_registration_start_page',
            properties: {
              convention_slug: p.conventionSlug,
              convention_product_id: p.conventionProductId,
              convention_name: p.conventionName,
              registration_option_id: p.registrationOptionId,
              registration_option_name: p.registrationOptionName,
              registrants_count: p.registrantsCount,
            },
          }
        },
      },
    },
  },
  'Selected convention registration ticket type': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'selected_convention_registration_ticket_type',
            properties: {
              convention_slug: p.conventionSlug,
              convention_product_id: p.conventionProductId,
              convention_name: p.conventionName,
              registration_option_id: p.registrationOptionId,
              registration_option_name: p.registrationOptionName,
              registrants_count: p.registrantsCount,
            },
          }
        },
      },
    },
  },
  'Submitted convention registration details': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'submitted_convention_registration_details',
            properties: {
              convention_slug: p.conventionSlug,
              convention_product_id: p.conventionProductId,
              convention_name: p.conventionName,
              registration_option_id: p.registrationOptionId,
              registration_option_name: p.registrationOptionName,
              registrants_count: p.registrantsCount,
              accepted_terms: p.acceptedTerms,
            },
          }
        },
      },
    },
  },
  'Viewed convention wait list page': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'viewed_convention_wait_list_page',
            properties: {
              convention_slug: p.conventionSlug,
              convention_product_id: p.conventionProductId,
              convention_name: p.conventionName,
              registration_option_id: p.registrationOptionId,
              registration_option_name: p.registrationOptionName,
            },
          }
        },
      },
    },
  },
  'Signed up for convention wait list': {
    defaultDestinations: ['kissmetrics', 'ga4', 'customerIo'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'signed_up_for_convention_wait_list',
            properties: {
              convention_slug: p.conventionSlug,
              convention_product_id: p.conventionProductId,
              convention_name: p.conventionName,
              registration_option_id: p.registrationOptionId,
              registration_option_name: p.registrationOptionName,
            },
          }
        },
      },
    },
  },
  'Visited checkout address page': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Entered address at checkout': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Used shipping address autocomplete': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Chose saved address at checkout': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Selected saved address at checkout': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Selected shipping option at checkout': {
    defaultDestinations: ['zohoPageSense'],
  },
  'Viewed wishlist': {
    defaultDestinations: ['kissmetrics'],
  },
  'Tracked package': {
    defaultDestinations: ['kissmetrics'],
  },
  'Contacted us': {
    defaultDestinations: ['kissmetrics'],
  },
  'Added product to wishlist': {
    defaultDestinations: ['kissmetrics'],
  },
  'Filtered category': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'filtered_category',
            properties: {
              category: p.Category ?? p.category,
              filter: p.Filter ?? p.filter,
            },
          }
        },
      },
    },
  },
  'Clicked top menu': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'top_menu_click',
            properties: {
              label: p.Label ?? p.label,
              url: p.Url ?? p.url,
            },
          }
        },
      },
    },
  },
  'Clicked top seller': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'top_seller_click',
            properties: {
              product: p['Product name'] ?? p.product,
              position: p.Position ?? p.position,
            },
          }
        },
      },
    },
  },
  'Clicked category top seller': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'top_seller_click',
            properties: {
              product: p['Product name'],
              position: p.Position,
              category: p.Category,
            },
          }
        },
      },
    },
  },
  'Logged out': {
    defaultDestinations: ['kissmetrics'],
  },
  'Logged in during checkout': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Deferred checkout cart merge': {
    defaultDestinations: ['kissmetrics'],
  },
  'Executed cart merge outside checkout': {
    defaultDestinations: ['kissmetrics'],
  },
  'Cart merge had partial skips': {
    defaultDestinations: ['kissmetrics'],
  },
  'Completed checkout after login': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Continued checkout as guest': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
  },
  'Started checkout with Apple Pay': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
    destinations: {
      zohoPageSense: { map: () => ({ eventName: 'Apple Pay' }) },
    },
  },
  'Completed checkout with Apple Pay': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
    destinations: {
      zohoPageSense: { map: () => ({ eventName: 'Apple Pay success' }) },
    },
  },
  'Failed checkout with Apple Pay': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
    destinations: {
      zohoPageSense: { map: () => ({ eventName: 'Apple Pay error' }) },
    },
  },
  'Received PayPal error': {
    defaultDestinations: ['kissmetrics', 'zohoPageSense'],
    destinations: {
      zohoPageSense: {
        map: event => {
          const label = (
            (event.properties?.pageSensePaymentLabel as string | undefined) ??
            'PayPal'
          ).trim()
          const paypalErr = event.properties?.['PayPal error']
          const isInstrumentDeclined =
            typeof paypalErr === 'string' &&
            paypalErr.includes('INSTRUMENT_DECLINED')
          // Old site: INSTRUMENT_DECLINED → `PayPal error`; other failures → `{Venmo|PayPal} error`.
          if (isInstrumentDeclined) {
            return { eventName: 'PayPal error' }
          }
          return { eventName: `${label} error` }
        },
      },
    },
  },
  /** Zoho PageSense only — old-site `pageSenseTag('Entered discount code')` (checkout). */
  'Entered discount code': {
    defaultDestinations: ['zohoPageSense'],
  },
  /** Zoho PageSense only — old-site pay page when `Message.Visible`. */
  'checkout error': {
    defaultDestinations: ['zohoPageSense'],
  },
  /** Zoho PageSense only — old-site Klarna submit on vanishinginc-dev/checkout/pay/Default.aspx. */
  'Started Klarna': {
    defaultDestinations: ['zohoPageSense'],
  },
  'Viewed blog post': {
    defaultDestinations: ['kissmetrics'],
  },
  'Viewed purchased product list': {
    defaultDestinations: ['kissmetrics'],
  },
  'Viewed digital library': {
    defaultDestinations: ['kissmetrics'],
  },
  'Visited creators area': {
    defaultDestinations: ['kissmetrics'],
  },
  'Viewed download product': {
    defaultDestinations: ['kissmetrics'],
  },
  'Visited free download landing page': {
    defaultDestinations: ['kissmetrics'],
  },
  'Visited newsletter preferences page': {
    defaultDestinations: ['kissmetrics'],
  },
  'Visited magic store learn page': {
    defaultDestinations: ['kissmetrics'],
  },
  'Entered magic store learn info': {
    defaultDestinations: ['kissmetrics'],
  },
  'Skipped magic store learn info': {
    defaultDestinations: ['kissmetrics'],
  },
  'Played download video': {
    defaultDestinations: ['kissmetrics'],
  },
  'Reached download video milestone': {
    defaultDestinations: ['kissmetrics'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          const productId = p['Download product ID'] ?? p['Product ID']
          return {
            eventName: 'Reached download video milestone',
            properties: {
              'Product ID': productId,
              'Download name': p['Download name'],
              'Download file ID': p['Download file ID'],
              Milestone: p.Milestone,
            },
          }
        },
      },
    },
  },
  'Downloaded file': {
    defaultDestinations: ['kissmetrics'],
    destinations: {
      kissmetrics: {
        map: event => {
          const p = event.properties || {}
          const productId = p['Download product ID'] ?? p['Product ID']
          return {
            eventName: 'Downloaded file',
            properties: {
              'Product ID': productId,
              'Download name': p['Download name'],
              'Download file ID': p['Download file ID'],
            },
          }
        },
      },
    },
  },
  'Clicked recommended product': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'recommended_product_click',
            properties: {
              product_id: p['Product id'] ?? p.productId,
              product_name: p['Product name'] ?? p.productName,
              source: p.Source ?? p.source,
            },
          }
        },
      },
    },
  },
  'Searched Discoverie': {
    defaultDestinations: ['kissmetrics'],
  },
  'Clicked Discoverie result': {
    defaultDestinations: ['kissmetrics'],
  },
  'Viewed studio library': {
    defaultDestinations: ['kissmetrics'],
  },
  'Viewed studio item': {
    defaultDestinations: ['kissmetrics'],
  },
  'Played studio video': {
    defaultDestinations: ['kissmetrics'],
  },
  'Reached studio video milestone': {
    defaultDestinations: ['kissmetrics'],
  },
  'Downloaded studio file': {
    defaultDestinations: ['kissmetrics'],
  },
  'Posted masterclass download question': {
    defaultDestinations: ['kissmetrics', 'ga4'],
    destinations: {
      ga4: {
        map: event => {
          const p = event.properties || {}
          return {
            eventName: 'posted_masterclass_question',
            properties: {
              product_id: p['Product ID'] ?? p.productId,
            },
          }
        },
      },
    },
  },
}
